/**
 * 어시스트 드로어 크기 — **순수 함수**(D2 결정 3·5).
 *
 * 훅과 분리한 이유: 범위 강제(clamp)가 이 기능의 본론이고, 그 규칙은
 * DOM·훅 없이 그대로 테스트할 수 있어야 한다. 저장은 px 로 한다 —
 * rem/vh 로 저장하면 루트 폰트 크기·뷰포트가 바뀔 때 의미가 이동한다.
 */

/** 접기 키(`omnitrans:assist-collapsed-viewer`)와 **다른 키**(D2 결정 5). */
export const ASSIST_SIZE_KEY = 'omnitrans:assist-size-viewer'

export interface AssistSize {
  /** px */
  w: number
  /** px */
  h: number
}

/** rem → px. 루트 폰트 크기를 읽지 못하면 16px. */
function remPx(): number {
  if (typeof document === 'undefined') return 16
  try {
    const raw = getComputedStyle(document.documentElement).fontSize
    const n = Number.parseFloat(raw)
    return Number.isFinite(n) && n > 0 ? n : 16
  } catch {
    return 16
  }
}

export interface SizeBounds {
  minW: number
  maxW: number
  minH: number
  maxH: number
}

/**
 * 현재 뷰포트에서 허용 범위. narrow 면 w 는 [vw, vw] 로 고정(조절 불가).
 *
 * 높이 상한이 `100vh - 8rem` 인 근거: 하단 `bottom-20`(5rem) + 상단 여유
 * 3rem 을 빼면 자막이 최소 20vh 남는다(D2 결정 3).
 */
export function sizeBounds(vw: number, vh: number, narrow: boolean): SizeBounds {
  const rem = remPx()
  const narrowW = Math.max(0, vw - 2.5 * rem)
  const minH = Math.min(16 * rem, Math.max(0, vh))
  const maxH = Math.max(minH, Math.min(0.8 * vh, vh - 8 * rem))
  if (narrow) {
    return { minW: narrowW, maxW: narrowW, minH, maxH }
  }
  const minW = Math.min(18 * rem, narrowW)
  const maxW = Math.max(minW, Math.min(36 * rem, 0.5 * vw))
  return { minW, maxW, minH, maxH }
}

/** 기본값 — 현행 기하(min(22rem, vw-2.5rem) × min(28rem, 60vh))와 같은 픽셀. */
export function defaultSize(vw: number, vh: number, narrow: boolean): AssistSize {
  const rem = remPx()
  const w = narrow
    ? Math.max(0, vw - 2.5 * rem)
    : Math.min(22 * rem, Math.max(0, vw - 2.5 * rem))
  const h = Math.min(28 * rem, 0.6 * vh)
  return { w, h }
}

function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v))
}

/**
 * 저장값 파싱 + 범위 강제. 잘못된 JSON·NaN·음수·범위 밖 전부 clamp 한
 * 값으로 돌려준다 — **던지지 않는다**.
 */
export function clampSize(raw: unknown, vw: number, vh: number,
                          narrow: boolean): AssistSize {
  const b = sizeBounds(vw, vh, narrow)
  const fallback = defaultSize(vw, vh, narrow)
  let parsed: unknown = raw
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = null
    }
  }
  const rec = (parsed && typeof parsed === 'object')
    ? (parsed as Record<string, unknown>)
    : null
  const pickW = rec && typeof rec.w === 'number' && Number.isFinite(rec.w)
    ? rec.w : fallback.w
  const pickH = rec && typeof rec.h === 'number' && Number.isFinite(rec.h)
    ? rec.h : fallback.h
  return {
    w: clampNum(pickW, b.minW, b.maxW),
    h: clampNum(pickH, b.minH, b.maxH),
  }
}
